<template>
    <div class="menu" :class="{ 'is-open': open }">
        <v-popover
            v-if="!isMobile"
            :placement="placement"
            :trigger="triggerType"
            :delay="{ show: 150, hide: 50 }"
            :popoverBaseClass="popoverClass"
            :offset="offset"
            boundariesElement="'offsetParent'"
            :open="open"
            @show="handleShow"
            @hide="handleHide"
        >
            <div
                class="menu-trigger"
                :class="{ outline }"
                data-testid="dropdown-actions-button"
                @click="onTriggerClick"
            >
                <slot name="trigger" :open="open">
                    <CoreButton class="trigger-button" :class="buttonSize">
                        <elm-menu-action-icon-icon
                            class="icon"
                            :class="iconColor === 'primary' ? 'primary-color' : 'info-color-40'"
                            :size="triggerIconSize"
                        />
                    </CoreButton>
                </slot>
            </div>
            <template #popover>
                <div
                    @mouseover="handleWhenInteractive(true)"
                    @mouseleave="handleWhenInteractive(false)"
                    @click="onContentClick"
                >
                    <slot></slot>
                </div>
            </template>
        </v-popover>

        <div v-if="isMobile" class="menu-trigger" :class="{ outline }" @click="onTriggerClick">
            <slot name="trigger" :open="open">
                <CoreButton class="trigger-button" :class="buttonSize">
                    <elm-menu-action-icon-icon
                        class="icon"
                        :class="iconColor === 'primary' ? 'primary-color' : 'info-color'"
                    />
                </CoreButton>
            </slot>
        </div>

        <div v-if="open && isMobile" class="menu-items-wrapper" @click="closeDropdown">
            <slot></slot>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Inject, Prop, Emit, Mixins } from 'vue-property-decorator';
    import vClickOutside from 'v-click-outside';
    import { Themes } from '@/common/classes';
    import { bodyOverflowHidden } from '@/common/services/dom-operations';
    import CoreButton from '@/common/components/buttons/CoreButton.vue';
    import { MobileModeStoreMixin } from '@/common/store/mobile-mode';
    import { backButtonService } from '@/common/services/mobile-fallback/native-back-button-service';
    import '@eloomi/icons/menu/menu-action-icon';
    import { IS_IN_MODAL } from '@/ui-kit/modal-window/context-keys';

    export type TriggerType = 'click' | 'hover' | 'manual';
    export type PlacementType =
        | 'auto'
        | 'auto-start'
        | 'auto-end'
        | 'top'
        | 'top-start'
        | 'top-end'
        | 'right'
        | 'right-start'
        | 'right-end'
        | 'bottom'
        | 'bottom-start'
        | 'bottom-end'
        | 'left'
        | 'left-start'
        | 'left-end';

    @Component({
        components: {
            CoreButton,
        },
        directives: {
            clickOutside: vClickOutside.directive,
        },
    })
    export default class CoreActionDropdown extends Mixins(MobileModeStoreMixin) {
        @Prop({ default: 'click' }) public triggers!: TriggerType[];
        @Prop({ default: 'bottom' }) public placement!: PlacementType;
        @Prop({ default: true }) public outline!: boolean;
        @Prop({ default: false }) public triggerSizeLarge!: boolean;
        @Prop({ default: false }) public interactive!: boolean;
        @Prop({ default: 9 }) public offset!: number | string;
        @Prop({ default: '' }) public popoverBaseClass!: string;
        /** Called when native back button is pressed. You can cancel closing of the dropdown by returning 'false' (e.g. if you have some inner navigation). */
        @Prop({ default: null }) public beforeBackButton!: (() => boolean) | null;
        @Prop({ default: false, type: Boolean }) public closeOnContentClick!: boolean;
        @Prop({ default: '24', type: String }) public triggerIconSize: boolean;
        @Prop({ default: false, type: Boolean }) public withBackground: boolean;

        public open = false;
        public iconColor: Themes = 'info';

        private nativeBackButtonActionCleanup: (() => void) | null = null;

        public onContentClick() {
            if (this.closeOnContentClick) {
                this.closeDropdown();
            }
        }

        public get triggerTypes() {
            return Array.isArray(this.triggers) ? this.triggers : [this.triggers];
        }

        @Inject({ from: IS_IN_MODAL, default: false }) readonly isInModal!: boolean;

        public get popoverClass() {
            const classes = [];
            if (this.popverBaseClass) {
                classes.push(this.popoverBaseClass);
            }
            if (this.isInModal) {
                classes.push('popover-in-modal');
            }
            return classes;
        }

        public get triggerType() {
            if (this.isMobile) {
                return this.triggerTypes.join(' ').replace('hover', '').trim();
            }

            return this.triggerTypes.includes('manual') ? 'manual' : this.triggerTypes.join(' ');
        }

        public get buttonSize() {
            if (this.triggerSizeLarge) {
                return 'large';
            }
            return '';
        }

        @Emit('open')
        public handleShow() {
            this.iconColor = 'primary';
        }

        @Emit('close')
        public handleHide() {
            this.iconColor = 'info';
            this.open = false;
        }

        public onTriggerClick() {
            if (this.triggerTypes.includes('click') || this.isMobile) {
                this.toggleDropdown();
            }
        }

        public toggleDropdown() {
            this.open ? this.closeDropdown() : this.openDropdown();
        }

        @Emit('close')
        public closeDropdown() {
            if (this.nativeBackButtonActionCleanup) {
                this.nativeBackButtonActionCleanup();
                this.nativeBackButtonActionCleanup = null;
            }

            this.open = false;
            this.iconColor = 'info';

            if (this.isMobile) {
                bodyOverflowHidden(false);
            }
        }

        @Emit('open')
        public openDropdown() {
            this.iconColor = 'primary';
            this.open = true;

            this.nativeBackButtonActionCleanup = backButtonService.pushAction(() => {
                if (this.beforeBackButton) {
                    const res = this.beforeBackButton();
                    if (res !== false) {
                        this.closeDropdown();
                    }
                    return res;
                } else {
                    this.closeDropdown();
                }
            });

            if (this.isMobile) {
                bodyOverflowHidden(true);
            }
        }

        public handleWhenInteractive(value) {
            if (this.interactive) {
                this.open = value;
                this.iconColor = value ? 'primary' : 'info';
            }
        }
    }
</script>

<style lang="less" scoped>
    @menu-item-wrapper-background-color: rgba(0, 0, 0, 0.5);

    .menu {
        position: relative;

        .v-popover {
            display: flex;
            height: 100%;

            ::v-deep > .trigger {
                min-width: 0;
            }
        }

        .menu-trigger:not(.outline) {
            height: 100%;

            .trigger-button {
                height: 100%;
                color: @dark-color;
                //background: @info-color-8;
                border: none;

                &:hover {
                    color: @primary-color;
                    //background: @primary-color-8;
                }

                &:hover > * {
                    color: @primary-color;
                }
            }
        }
    }

    .trigger-button {
        width: 35px;
        height: 35px;
        color: @primary-color;
        background-color: @transparent;
        border: 2px solid @primary-color;
        border-radius: @border-round;
        transition: @generic-transition;
        .flex-center-content();

        ::v-deep.name {
            padding: 0;
        }

        &:hover {
            color: @bright-color;
            //background-color: @primary-color;
            cursor: pointer;
        }

        .el-action-icon {
            position: initial;
            width: auto;
            height: auto;
            cursor: pointer;
            .flex-center-content-vertically();
        }
    }

    .large {
        width: 46px;
        height: 46px;

        .name {
            .el-action-icon {
                font-size: 24px;
            }
        }
    }

    .menu-items-wrapper {
        position: absolute;
        top: 100%;
        left: -8px;
        z-index: 9999;
        padding-top: @spacing-8;

        @media screen and (max-width: 768px) {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 9999;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            height: 100%;
            padding-top: 0;
            overflow: hidden;
            background-color: @menu-item-wrapper-background-color;

            .menu .menu-trigger:not(.outline) {
                margin-left: 0;
            }
        }
    }
</style>

<style lang="less">
    .vue-popover-theme {
        z-index: @header-z-index - 1;

        &:focus {
            outline: none;
        }

        &[aria-hidden='true'] {
            visibility: hidden;
            opacity: 0;
            transition: opacity 0.15s, visibility 0.15s;
        }

        &[aria-hidden='false'] {
            visibility: visible;
            opacity: 1;
            transition: opacity 0.15s;
        }
    }

    .course-table-header-dropdown {
        z-index: 1;
    }

    .icon {
        .generic-transition;

        &:hover {
            color: @primary-color;
        }
    }

    .popover-in-modal {
        z-index: @popover-in-modal-z-index;
    }
</style>
